import { api } from "@/lib/api";
import { fmtMoney, fmtDate } from "@/lib/format";
import { Printer } from "@phosphor-icons/react";

function escHtml(s) {
  return String(s ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;");
}

function numToIndianWords(n) {
  n = Math.round(Number(n) || 0);
  if (n === 0) return "Zero";
  const ones = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine"];
  const teens = ["Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
  const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
  const twoDigits = (x) => {
    if (x < 10) return ones[x];
    if (x < 20) return teens[x - 10];
    return tens[Math.floor(x / 10)] + (x % 10 ? " " + ones[x % 10] : "");
  };
  const threeDigits = (x) => {
    const h = Math.floor(x / 100);
    const rest = x % 100;
    return (h ? ones[h] + " Hundred" : "") + (rest ? (h ? " " : "") + twoDigits(rest) : "");
  };
  const crore = Math.floor(n / 10000000); n %= 10000000;
  const lakh = Math.floor(n / 100000); n %= 100000;
  const thousand = Math.floor(n / 1000); n %= 1000;
  const parts = [];
  if (crore) parts.push(threeDigits(crore) + " Crore");
  if (lakh) parts.push(twoDigits(lakh) + " Lakh");
  if (thousand) parts.push(twoDigits(thousand) + " Thousand");
  if (n) parts.push(threeDigits(n));
  return parts.join(" ").trim() + " Only";
}

function buildHtml(booking, settings) {
  const c = booking.customer || {};
  const v = booking.vehicle;
  // Show the package name first (operator-facing label like JUPITER 125 BSVI-DISC)
  // and fall back to make+model if no package is linked. VIN is intentionally
  // OMITTED — booking is package+color, the specific unit isn't decided until
  // delivery.
  const itemLabel = booking.package_name || v?.package_name || "—";
  const totalPaid = Number(booking.total_paid) || 0;

  const paymentRows = (booking.payments || []).map((p, idx) => `<tr>
    <td>${idx + 1}</td>
    <td>${escHtml(fmtDate(p.paid_on))}</td>
    <td style="text-transform:uppercase">${escHtml(p.payment_method)}</td>
    <td class="mono">${escHtml(p.reference || "—")}</td>
    <td>${escHtml(p.note || "—")}</td>
    <td class="num">${escHtml(fmtMoney(p.amount))}</td>
  </tr>`).join("");

  return `<!doctype html><html><head><title>Booking Receipt · ${escHtml(booking.id.slice(0, 8).toUpperCase())}</title>
<style>
  *{box-sizing:border-box}
  body{font-family:'Times New Roman',serif;color:#111;margin:24px;background:#fff;max-width:780px}
  .hdr{display:flex;align-items:flex-start;gap:20px;border-bottom:2px solid #000;padding-bottom:10px;margin-bottom:10px}
  .logo{max-height:64px;max-width:160px;object-fit:contain}
  .dealer{flex:1}
  .dealer b{font-size:16px}
  .title{flex:1;text-align:right;font-family:'Times New Roman',serif}
  .title h1{margin:0;font-size:20px;letter-spacing:2px}
  .title .sub{font-family:'IBM Plex Mono',monospace;font-size:10px;color:#555;text-transform:uppercase;letter-spacing:.15em;margin-top:4px}
  .meta{display:flex;justify-content:space-between;font-size:11px;margin:6px 0}
  .card{border:1px solid #000;padding:10px;margin:10px 0;display:grid;grid-template-columns:1fr 1fr;gap:10px}
  .card-title{font-family:'IBM Plex Mono',monospace;font-size:10px;letter-spacing:.2em;text-transform:uppercase;color:#666;grid-column:1/-1;margin-bottom:4px}
  .kv{font-size:11.5px;padding:3px 0;border-bottom:1px dashed #ddd}
  .kv .lbl{color:#555;display:inline-block;min-width:100px}
  table.payments{width:100%;border-collapse:collapse;margin-top:6px}
  table.payments th,table.payments td{padding:6px 8px;border-bottom:1px solid #eee;font-size:11.5px;text-align:left}
  table.payments th{font-family:'IBM Plex Mono',monospace;font-size:10px;letter-spacing:.15em;text-transform:uppercase;color:#555;border-bottom:1px solid #000;background:#fafafa}
  td.num,th.num{text-align:right;font-family:'Courier New',monospace}
  .mono{font-family:'Courier New',monospace;font-size:11px}
  .totals{margin-top:12px;border:1px solid #000}
  .totals .row{display:flex;justify-content:space-between;padding:6px 10px;border-bottom:1px solid #ddd}
  .totals .row:last-child{border-bottom:0;background:#fafafa;border-top:2px solid #000}
  .totals .lbl{font-family:'IBM Plex Mono',monospace;font-size:11px;text-transform:uppercase;letter-spacing:.1em;color:#555}
  .totals .amt{font-family:'Courier New',monospace;font-size:14px;font-weight:700}
  .words{margin:8px 0;padding:6px 10px;background:#fafafa;font-size:11.5px;border:1px solid #ddd}
  .signatures{display:flex;justify-content:space-between;margin-top:40px;font-size:11px}
  .sig{width:45%}
  .sig-line{border-bottom:1px solid #000;height:30px;margin-bottom:4px}
  .foot{margin-top:30px;font-family:'IBM Plex Mono',monospace;font-size:9px;color:#888;text-transform:uppercase;letter-spacing:.15em;text-align:center}
  @media print{button{display:none}}
</style></head><body>
<div class="hdr">
  ${settings.logo_url ? `<img class="logo" src="${escHtml(settings.logo_url)}" alt="logo"/>` : ""}
  <div class="dealer">
    <b>${escHtml(settings.name || "DEALERSHIP")}</b><br/>
    <span style="font-size:11px">${escHtml(settings.address || "")}</span><br/>
    <span style="font-size:11px">State: ${escHtml(settings.state_name || "—")} · Code: ${escHtml(settings.state_code || "—")}</span><br/>
    <span style="font-size:11px">GSTIN: <b>${escHtml(settings.gstin || "—")}</b></span>
  </div>
  <div class="title">
    <h1>MONEY RECEIPT</h1>
    <div class="sub">Booking Advance · For Vehicle Reservation</div>
  </div>
</div>
<div class="meta">
  <div>Booking No: <b>BKG-${escHtml(booking.id.slice(0, 8).toUpperCase())}</b></div>
  <div>Date: <b>${escHtml(fmtDate(booking.created_at))}</b></div>
</div>

<div class="card">
  <div class="card-title">Customer Details</div>
  <div>
    <div class="kv"><span class="lbl">Name</span> : <b>${escHtml(c.name || "—")}</b></div>
    <div class="kv"><span class="lbl">Phone</span> : ${escHtml(c.phone || "—")}</div>
    <div class="kv"><span class="lbl">Email</span> : ${escHtml(c.email || "—")}</div>
  </div>
  <div>
    <div class="kv"><span class="lbl">Address</span> : ${escHtml(c.address || "—")}</div>
    <div class="kv"><span class="lbl">Cust GSTIN</span> : ${escHtml(c.gst_number || c.gstin || "—")}</div>
  </div>
</div>

<div class="card">
  <div class="card-title">Booking For</div>
  <div>
    <div class="kv"><span class="lbl">Model</span> : <b>${escHtml(itemLabel)}</b></div>
  </div>
  <div>
    <div class="kv"><span class="lbl">Color</span> : <b>${escHtml(booking.color || "—")}</b></div>
  </div>
</div>

<div style="font-family:'IBM Plex Mono',monospace;font-size:10px;letter-spacing:.2em;text-transform:uppercase;color:#666;margin-top:14px;margin-bottom:4px">Payments Received</div>
<table class="payments">
  <thead><tr>
    <th>#</th>
    <th>Date</th>
    <th>Method</th>
    <th>Reference</th>
    <th>Note</th>
    <th class="num">Amount</th>
  </tr></thead>
  <tbody>
    ${paymentRows || `<tr><td colspan="6" style="text-align:center;color:#888;padding:18px">No payments captured yet.</td></tr>`}
  </tbody>
</table>

<div class="totals">
  <div class="row"><span class="lbl">Total Advance Paid</span><span class="amt" style="color:#00713a">${escHtml(fmtMoney(totalPaid))}</span></div>
</div>

<div class="words">
  Amount Received In Words: <b>${escHtml(numToIndianWords(totalPaid))}</b>
</div>

<div style="font-family:'IBM Plex Mono',monospace;font-size:10px;color:#666;margin-top:10px">
  Note: Final on-road price is fixed on the day of delivery as per the prevailing pricelist.
  Customer may switch to another available variant; the advance shown above will be carried over.
</div>

<div class="signatures">
  <div class="sig"></div>
  <div class="sig" style="text-align:right">
    <div class="sig-line"></div>
    <div>For ${escHtml(settings.name || "Dealership")}<br/>Authorised Signatory</div>
  </div>
</div>

<div class="foot">Computer-generated receipt · ${escHtml(new Date().toLocaleString())}</div>
<script>window.onload=function(){window.print();}</script>
</body></html>`;
}

// Cache settings between clicks within a session.
let _settingsCache = null;
async function loadSettings() {
  if (_settingsCache) return _settingsCache;
  try {
    const { data } = await api.get("/settings/dealership");
    _settingsCache = data || {};
  } catch {
    _settingsCache = {};
  }
  return _settingsCache;
}

export default function BookingReceiptButton({ booking }) {
  const handle = async () => {
    const settings = await loadSettings();
    const w = window.open("", "_blank", "width=900,height=1100");
    if (!w) return;
    w.document.write(buildHtml(booking, settings));
    w.document.close();
  };

  return (
    <button
      type="button"
      onClick={handle}
      data-testid={`booking-receipt-${booking.id}`}
      title="Print booking receipt"
      className="p-1.5 border border-canvas-line hover:border-ink"
    >
      <Printer size={12} weight="bold" />
    </button>
  );
}
